import { useState, type FormEvent, type ReactNode } from "react";
import { Icon } from "../components/Icon";
import { Notice, useNotice } from "../components/Notice";
import { PageHeader } from "../components/PageHeader";
import { buildQuote, estimateLines, updateQuoteDraft, type DraftLine, type QuoteDetail, type QuoteDraft } from "../lib/agreements-data";
import { aud, localInstant, localYmd } from "../lib/format";
import { ClientPicker } from "../modules/clients/ClientParts";

function Field({ label, hint, wide, children }: { label: string; hint?: string; wide?: boolean; children: ReactNode }) {
  return (
    <label className={`field${wide ? " field--wide" : ""}`}>
      <span className="field__label">{label}</span>
      {children}
      {hint && <span className="field__hint">{hint}</span>}
    </label>
  );
}

type Line = DraftLine & { key: number };
let keySeq = 0;
const withKey = (l: DraftLine): Line => ({ ...l, key: ++keySeq });
const money = (n: number) => Math.round(n * 100) / 100;

/** Price one shift from the rate card and add its lines, multiplied by the number of shifts. */
function RateCardHelper({ onAdd }: { onAdd: (lines: DraftLine[], warnings: string[]) => void }) {
  const [date, setDate] = useState(localYmd());
  const [start, setStart] = useState("07:00");
  const [hours, setHours] = useState(8);
  const [crew, setCrew] = useState(2);
  const [shifts, setShifts] = useState(1);
  const [busy, setBusy] = useState(false);
  const { notice, setNotice, fail } = useNotice();

  async function add() {
    setBusy(true);
    setNotice(null);
    try {
      const r = await estimateLines({ startsAt: localInstant(date, start).toISOString(), hours, crewSize: crew });
      onAdd(r.lines.map((l) => ({ ...l, quantity: money(l.quantity * shifts) })), r.warnings);
    } catch (e) {
      fail(e);
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="stack" style={{ gap: 10 }}>
      <Notice notice={notice} onDismiss={() => setNotice(null)} />
      <div className="form-grid">
        <Field label="First shift date"><input className="input" type="date" value={date} onChange={(e) => setDate(e.target.value)} /></Field>
        <Field label="Start"><input className="input" type="time" value={start} onChange={(e) => setStart(e.target.value)} /></Field>
        <Field label="Hours per shift"><input className="input" type="number" min={1} max={24} step={0.5} value={hours} onChange={(e) => setHours(Number(e.target.value))} /></Field>
        <Field label="Crew per shift"><input className="input" type="number" min={1} max={20} value={crew} onChange={(e) => setCrew(Number(e.target.value))} /></Field>
        <Field label="Number of shifts" hint="Same rate for each"><input className="input" type="number" min={1} max={60} value={shifts} onChange={(e) => setShifts(Number(e.target.value))} /></Field>
      </div>
      <div><button type="button" className="btn btn--ink btn--sm" disabled={busy} aria-busy={busy} onClick={() => void add()}><Icon name="plus" size={16} /> {busy ? "Pricing" : "Add priced lines"}</button></div>
    </div>
  );
}

export function QuoteEditor({ existing }: { existing?: QuoteDetail }) {
  const [f, setF] = useState({
    clientName: existing?.clientName ?? "",
    clientContact: existing?.clientContact ?? "",
    clientEmail: existing?.clientEmail ?? "",
    jobTitle: existing?.jobTitle ?? "",
    siteAddress: existing?.siteAddress ?? "",
    schedule: existing?.schedule ?? "",
    crewSummary: existing?.crewSummary ?? "",
    notes: existing?.notes ?? "",
    validDays: 30,
  });
  const [customerId, setCustomerId] = useState<string | null>(existing?.customerId ?? null);
  const [lines, setLines] = useState<Line[]>((existing?.lines ?? []).map((l) => withKey({ code: l.code, description: l.description, quantity: l.quantity, unit: l.unit, unitPrice: l.unitPrice })));
  const [helper, setHelper] = useState(!existing);
  const [busy, setBusy] = useState(false);
  const { notice, setNotice, fail } = useNotice();
  const set = (k: keyof typeof f) => (e: { target: { value: string } }) => setF((x) => ({ ...x, [k]: e.target.value }));
  const setLine = (key: number, patch: Partial<DraftLine>) => setLines((ls) => ls.map((l) => (l.key === key ? { ...l, ...patch } : l)));
  const subtotal = lines.reduce((s, l) => s + money(l.quantity * l.unitPrice), 0);

  async function submit(e: FormEvent) {
    e.preventDefault();
    if (!f.clientName.trim() || !f.jobTitle.trim()) return setNotice({ tone: "error", text: "Add the client and a name for the job." });
    const clean = lines.filter((l) => l.description.trim());
    if (!clean.length) return setNotice({ tone: "error", text: "Add at least one line." });
    const draft: QuoteDraft = {
      customerId: customerId ?? undefined,
      leadId: existing?.leadId ?? undefined,
      clientName: f.clientName,
      clientContact: f.clientContact,
      clientEmail: f.clientEmail,
      jobTitle: f.jobTitle,
      siteAddress: f.siteAddress,
      schedule: f.schedule,
      crewSummary: f.crewSummary,
      notes: f.notes,
      validDays: Number(f.validDays) || 30,
      lines: clean.map(({ key: _k, ...l }) => ({ ...l, quantity: Number(l.quantity), unitPrice: Number(l.unitPrice) })),
    };
    setBusy(true);
    try {
      const id = existing ? (await updateQuoteDraft(existing.id, draft)).quote.id : (await buildQuote(draft)).id;
      window.location.hash = `#/quotes/${id}`;
    } catch (err) {
      fail(err);
      setBusy(false);
    }
  }

  return (
    <section className="page" aria-labelledby="page-title">
      <a className="backlink" href={existing ? `#/quotes/${existing.id}` : "#/quotes"}><Icon name="arrow" size={16} /> {existing ? existing.quoteNumber : "All quotes"}</a>
      <PageHeader
        eyebrow="Quotes"
        title={existing ? `Edit ${existing.quoteNumber}` : "New quote"}
        meta={existing ? "Saving changes cancels any signing link already sent. Send it again afterwards." : "The client sees exactly this when they sign: the job, the lines and the totals, with your terms."}
      />
      <form className="job-grid" onSubmit={submit}>
        <Notice notice={notice} onDismiss={() => setNotice(null)} />
        <div className="card">
          <div className="card__head"><h2 className="card__title">Client and job</h2></div>
          <div className="card__body form-grid">
            <ClientPicker value={customerId} onPick={(c) => {
              setCustomerId(c?.id ?? null);
              if (c) setF((x) => ({ ...x, clientName: c.legal_name ?? c.name, clientContact: c.contact_name ?? x.clientContact, clientEmail: c.email ?? x.clientEmail }));
            }} />
            <Field label="Client business"><input className="input" required value={f.clientName} onChange={set("clientName")} placeholder="e.g. Example Builders Pty Ltd" /></Field>
            <Field label="Contact person"><input className="input" value={f.clientContact} onChange={set("clientContact")} /></Field>
            <Field label="Contact email" hint="Where the signing link goes"><input className="input" type="email" value={f.clientEmail} onChange={set("clientEmail")} /></Field>
            <Field label="Job"><input className="input" required value={f.jobTitle} onChange={set("jobTitle")} placeholder="e.g. Lane closure for crane lift" /></Field>
            <Field label="Site address"><input className="input" value={f.siteAddress} onChange={set("siteAddress")} /></Field>
            <Field label="Crew"><input className="input" value={f.crewSummary} onChange={set("crewSummary")} placeholder="e.g. 4 traffic controllers" /></Field>
            <Field label="Dates and times" wide><textarea className="input" rows={3} value={f.schedule} onChange={set("schedule")} placeholder="e.g. Thursday 22 and Saturday 24 October, 9am to 4pm" /></Field>
          </div>
        </div>

        <div className="card">
          <div className="card__head">
            <h2 className="card__title">Price</h2>
            <button type="button" className="btn btn--outline btn--sm" onClick={() => setHelper((h) => !h)}>{helper ? "Hide rate card" : "Price from rate card"}</button>
          </div>
          <div className="card__body stack">
            {helper && (
              <RateCardHelper
                onAdd={(ls, warnings) => {
                  setLines((cur) => [...cur, ...ls.map(withKey)]);
                  setNotice(warnings.length ? { tone: "error", text: warnings.join(" ") } : { tone: "ok", text: "Lines added from the rate card. Adjust rates for any negotiated price." });
                }}
              />
            )}
            <div className="table-wrap table-wrap--flat">
              <table className="table table--stack">
                <thead><tr><th scope="col">Description</th><th scope="col">Qty</th><th scope="col">Unit</th><th scope="col">Rate ex GST</th><th scope="col" className="n">Amount</th><th scope="col"><span className="sr-only">Remove</span></th></tr></thead>
                <tbody>
                  {lines.map((l) => (
                    <tr key={l.key}>
                      <td data-label="Description"><input className="input" aria-label="Description" value={l.description} onChange={(e) => setLine(l.key, { description: e.target.value })} /></td>
                      <td data-label="Qty"><input className="input" aria-label="Quantity" type="number" step="0.25" min={0} value={l.quantity} onChange={(e) => setLine(l.key, { quantity: Number(e.target.value) })} style={{ maxWidth: 110 }} /></td>
                      <td data-label="Unit"><input className="input" aria-label="Unit" value={l.unit} onChange={(e) => setLine(l.key, { unit: e.target.value })} style={{ maxWidth: 110 }} /></td>
                      <td data-label="Rate"><input className="input" aria-label="Rate excluding GST" type="number" step="0.01" value={l.unitPrice} onChange={(e) => setLine(l.key, { unitPrice: Number(e.target.value) })} style={{ maxWidth: 130 }} /></td>
                      <td data-label="Amount" className="n strong">{aud(money(l.quantity * l.unitPrice))}</td>
                      <td className="actions"><button type="button" className="btn btn--outline btn--xs" onClick={() => setLines((ls) => ls.filter((x) => x.key !== l.key))}>Remove</button></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="form-actions">
              <button type="button" className="btn btn--outline btn--sm" onClick={() => setLines((ls) => [...ls, withKey({ description: "", quantity: 1, unit: "each", unitPrice: 0 })])}><Icon name="plus" size={16} /> Add line</button>
              <span className="strong">Subtotal {aud(subtotal)} ex GST, about {aud(money(subtotal * 1.1))} inc GST</span>
            </div>
          </div>
        </div>

        <div className="card">
          <div className="card__head"><h2 className="card__title">Notes and validity</h2></div>
          <div className="card__body form-grid">
            <Field label="Notes for the client" wide><textarea className="input" rows={3} value={f.notes} onChange={set("notes")} /></Field>
            <Field label="Valid for (days)"><input className="input" type="number" min={1} max={90} value={f.validDays} onChange={set("validDays")} /></Field>
          </div>
        </div>

        <div className="form-actions">
          <button type="submit" className="btn btn--primary" disabled={busy} aria-busy={busy}>{busy ? "Saving" : existing ? "Save changes" : "Create quote"}</button>
          <a className="btn btn--outline" href={existing ? `#/quotes/${existing.id}` : "#/quotes"}>Cancel</a>
        </div>
      </form>
    </section>
  );
}
